import { type ReactElement } from 'react'
import type { AlertProps } from '@mui/material'
import { Alert, Stack, Typography } from '@mui/material'
import type { ThreatAnalysisResults } from '@safe-global/utils/features/safe-shield/types'
import type { AsyncResult } from '@safe-global/utils/hooks/useAsync'
import ExternalLink from '@/components/common/ExternalLink'
import { Severity } from '@safe-global/utils/features/safe-shield/types'
import { useHypernativeOAuth } from '@/features/hypernative/hooks/useHypernativeOAuth'
import { useAssessmentUrl } from '@/features/hypernative/hooks/useAssessmentUrl'
import { useHnAssessmentSeverity } from '@/features/hypernative/hooks/useHnAssessmentSeverity'
import LockIcon from '@/public/images/common/lock-small.svg'

interface HnQueueAssessmentBannerProps {
  safeTxHash: string
  assessment: AsyncResult<ThreatAnalysisResults> | undefined
  isAuthenticated: boolean
}

const SEVERITY_MESSAGES: Record<Severity, string> = {
  [Severity.OK]: 'No issues found by Hypernative Guardian.',
  [Severity.INFO]: 'Info available from Hypernative Guardian.',
  [Severity.WARN]: 'Issues found by Hypernative Guardian.',
  [Severity.CRITICAL]: 'Transaction was blocked by Hypernative Guardian.',
  [Severity.ERROR]: 'Unable to fetch security scan result.',
}

const ALERT_SEVERITIES: Record<Severity, AlertProps['severity']> = {
  [Severity.OK]: 'success',
  [Severity.INFO]: 'info',
  [Severity.WARN]: 'warning',
  [Severity.CRITICAL]: 'error',
  [Severity.ERROR]: 'error',
}

export const HnQueueAssessmentBanner = ({
  safeTxHash,
  assessment,
  isAuthenticated,
}: HnQueueAssessmentBannerProps): ReactElement | null => {
  const { initiateLogin } = useHypernativeOAuth()
  const severity = useHnAssessmentSeverity(assessment)
  const assessmentUrl = useAssessmentUrl(safeTxHash)

  if (!isAuthenticated) {
    const handleLogin = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      e.stopPropagation()
      initiateLogin()
    }

    return (
      <Alert severity="background" icon={<LockIcon />}>
        <Stack gap={1}>
          <Typography variant="body2" color="text.secondary">
            Log in to Hypernative to view security scan result.
          </Typography>
          <ExternalLink
            onClick={handleLogin}
            href="#"
            noIcon={false}
            sx={{
              textDecoration: 'underline',
              display: 'inline-flex',
              alignSelf: 'flex-start',
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Log in
            </Typography>
          </ExternalLink>
        </Stack>
      </Alert>
    )
  }

  if (!severity) {
    return null
  }

  const message = SEVERITY_MESSAGES[severity]
  const alertSeverity = ALERT_SEVERITIES[severity]

  return (
    <Alert severity={alertSeverity}>
      <Stack gap={1}>
        <Typography variant="body2">{message}</Typography>
        <ExternalLink
          onClick={(e) => e.stopPropagation()}
          href={assessmentUrl}
          sx={{
            textDecoration: 'underline',
            display: 'inline-flex',
            alignSelf: 'flex-start',
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            View details
          </Typography>
        </ExternalLink>
      </Stack>
    </Alert>
  )
}
